import {
  fetchExternalAwsAccountById,
  fetchExternalAwsResourceById,
  getEncryptionAlgorithm,
  getInstanceSpecs,
  getStorageIops,
  selectExternalAwsAccountById,
  selectExternalAwsResourceById,
} from "@app/deploy";
import { useLoader, useQuery, useSelector } from "@app/react";
import {
  externalAwsResourceConnectionsUrl,
  externalAwsResourceSettingsUrl,
  settingsAccountIntegrationsAwsResourcesUrl,
  settingsAccountIntegrationsUrl,
} from "@app/routes";
import type {
  DeployExternalAwsAccount,
  DeployExternalAwsResource,
} from "@app/types";
import { Link, Outlet, useParams } from "react-router-dom";
import {
  DetailHeader,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHeaderView,
  DetailTitleBar,
  ExternalLink,
  IconAws,
  IconExternalLink,
  LoadingSpinner,
  type TabItem,
} from "../shared";
import { AppSidebarLayout } from "./app-sidebar-layout";

export function ExternalAwsResourceHeader({
  account,
  resource,
  isLoading,
}: {
  account: DeployExternalAwsAccount;
  resource: DeployExternalAwsResource;
  isLoading: boolean;
}) {
  const databaseType = resource.metadata?.engine || "unknown";

  // Map database type to image name
  const imageType = databaseType === "postgres" ? "postgresql" : databaseType;

  // Determine image URL based on whether type is unknown
  const imageUrl =
    databaseType === "unknown"
      ? "/resource-types/logo-unknown.png"
      : `/database-types/logo-${imageType}.png`;

  return (
    <DetailHeader>
      <DetailTitleBar
        docsUrl="#"
        title="Database Details"
        isLoading={isLoading}
        icon={
          resource.id === "" ? (
            <LoadingSpinner />
          ) : (
            <img
              src={imageUrl}
              className="w-[32px] h-[32px] mr-3"
              aria-label={`${databaseType} Database`}
            />
          )
        }
      />

      <DetailInfoGrid columns={4}>
        <DetailInfoItem title="ID">
          {resource.id === "" ? <LoadingSpinner /> : resource.id}
        </DetailInfoItem>

        <DetailInfoItem title="Disk IOPS">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            getStorageIops(
              resource.metadata?.storage_type || "",
              resource.metadata?.allocated_storage,
            )
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Memory Limit">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            getInstanceSpecs(resource.metadata?.db_instance_class || "").memory
          )}
        </DetailInfoItem>

        <DetailInfoItem title="AWS RDS Database ID">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <ExternalLink
              href={`https://${resource.region}.console.aws.amazon.com/rds/home?region=${resource.region}#database:id=${resource.metadata?.db_instance_identifier};is-cluster=false`}
            >
              <span className="leading-5 pt-1">
                {resource.metadata?.dbi_resource_id}
              </span>
              <IconExternalLink
                variant="sm"
                className="inline pt-0 ml-1 h-5 mb-1 stroke-current"
              />
            </ExternalLink>
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Type">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            databaseType
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Disk Type">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            resource.metadata?.storage_type?.toUpperCase() || "Unknown"
          )}
        </DetailInfoItem>

        <DetailInfoItem title="CPU Share">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            getInstanceSpecs(resource.metadata?.db_instance_class || "").cpu
          )}
        </DetailInfoItem>

        <DetailInfoItem title="AWS Account ID">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <Link to={settingsAccountIntegrationsAwsResourcesUrl(account.id)}>
              {account.awsAccountId}
            </Link>
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Disk Size">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            (resource.metadata?.allocated_storage &&
              `${resource.metadata?.allocated_storage} GB`) ||
            "Unknown"
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Disk Encryption">
          {Object.keys(resource.metadata)?.length === 0 ? (
            <LoadingSpinner />
          ) : (
            getEncryptionAlgorithm(
              resource.metadata?.storage_encrypted || false,
            )
          )}
        </DetailInfoItem>

        <DetailInfoItem title="Provider">
          <ExternalLink
            href={`https://${account.awsAccountId}.signin.aws.amazon.com/console`}
          >
            <IconAws className="inline mr-2" />
            <span className="leading-5 pt-1">{account.awsAccountId}</span>
            <IconExternalLink
              variant="sm"
              className="inline pt-0 ml-1 h-5 mb-1 stroke-current"
            />
          </ExternalLink>
        </DetailInfoItem>

        <DetailInfoItem title="AWS Account Name">
          {account.id === "" ? <LoadingSpinner /> : account.accountName}
        </DetailInfoItem>
      </DetailInfoGrid>
    </DetailHeader>
  );
}

function ExternalAwsResourcePageHeader() {
  const { id = "" } = useParams();
  const resource = useSelector((s) => selectExternalAwsResourceById(s, { id }));
  const account = useSelector((s) =>
    selectExternalAwsAccountById(s, { id: resource.externalAwsAccountId }),
  );
  const loader = useQuery(fetchExternalAwsResourceById({ id }));
  useQuery(fetchExternalAwsAccountById({ id: resource.externalAwsAccountId }));
  const load = useLoader(fetchExternalAwsResourceById({ id }));

  const tabs: TabItem[] = [
    { name: "Connected Apps", href: externalAwsResourceConnectionsUrl(id) },
    { name: "Settings", href: externalAwsResourceSettingsUrl(id) },
  ];

  return (
    <DetailPageHeaderView
      {...loader}
      breadcrumbs={[
        {
          name: "Account Integrations",
          to: settingsAccountIntegrationsUrl(),
        },
        {
          name: account.accountName,
          to: settingsAccountIntegrationsAwsResourcesUrl(account.id),
        },
      ]}
      title={resource.resourceName || resource.id}
      detailsBox={
        <ExternalAwsResourceHeader
          account={account}
          resource={resource}
          isLoading={load.isLoading}
        />
      }
      tabs={tabs}
      lastBreadcrumbTo={externalAwsResourceConnectionsUrl(resource.id)}
    />
  );
}

export const ExternalAwsResourceDetailLayout = () => {
  return (
    <AppSidebarLayout header={<ExternalAwsResourcePageHeader />}>
      <Outlet />
    </AppSidebarLayout>
  );
};
